/**
 * User Profiles API - Mudo Network
 * 
 * Functions for fetching user profile information
 */

import { supabase } from '../../supabaseClient';
import { getCachedUser } from '../../contexts/AuthContext';

export interface UserProfile {
  id: string;
  display_name?: string;
  avatar_url?: string;
  avatar_emoji?: string;
  bio?: string;
  country?: string;
  created_at: string;
}

const USER_PUBLIC_SELECT =
  'id,display_name,country,avatar_url,avatar_emoji,bio,trial_started_at,trial_ends_at,access_until,marketing_opt_in,created_at';

/**
 * Get user profile by ID
 */
export async function getUserProfile(userId: string): Promise<{ data: UserProfile | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('users_public')
      .select(USER_PUBLIC_SELECT)
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return { data: null, error };
  }
}

/**
 * Get multiple user profiles by IDs
 */
export async function getUserProfiles(userIds: string[]): Promise<{ data: UserProfile[] | null; error: any }> {
  try {
    if (userIds.length === 0) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('users_public')
      .select(USER_PUBLIC_SELECT)
      .in('id', userIds);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching user profiles:', error);
    return { data: null, error };
  }
}

/**
 * Update current user's profile
 */
export async function updateUserProfile(updates: Partial<UserProfile>): Promise<{ data: UserProfile | null; error: any }> {
  try {
    // Get current user from centralized auth state (no direct supabase.auth call)
    const user = getCachedUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const buildPayload = (includeAvatar: boolean) => {
      const payload: Record<string, any> = {
        id: user.id,
        display_name: updates.display_name,
      };
      if (updates.country) payload.country = updates.country;
      if (updates.bio) payload.bio = updates.bio;
      if (includeAvatar) {
        if (updates.avatar_url !== undefined) payload.avatar_url = updates.avatar_url;
        if (updates.avatar_emoji !== undefined) payload.avatar_emoji = updates.avatar_emoji;
      }
      return payload;
    };

    const attempt = async (includeAvatar: boolean) => {
      const payload = buildPayload(includeAvatar);
      const { data, error } = await supabase
        .from('users_public')
        .upsert(payload, { onConflict: 'id' })
        .select(USER_PUBLIC_SELECT)
        .maybeSingle();
      if (error) throw error;
      return data;
    };

    let data: any = null;
    try {
      data = await attempt(true);
    } catch (err: any) {
      const msg = String(err?.message || "").toLowerCase();
      const looksLikeSchemaMismatch =
        msg.includes("column") || msg.includes("schema") || msg.includes("does not exist");
      if (!looksLikeSchemaMismatch) throw err;
      // Fall back in environments without avatar columns.
      data = await attempt(false);
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { data: null, error };
  }
}

/**
 * Get current user's profile
 */
export async function getCurrentUserProfile(): Promise<{ data: UserProfile | null; error: any }> {
  try {
    // Get current user from centralized auth state (no direct supabase.auth call)
    const user = getCachedUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    return getUserProfile(user.id);
  } catch (error) {
    console.error('Error fetching current user profile:', error);
    return { data: null, error };
  }
}
